import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, X } from 'lucide-react';
import {
  uploadDocumentoImpianto,
  type DocumentoImpianto,
  type DocumentoImpiantoTipo,
} from '../../api/documentiImpianto';

const inputCls =
  'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';
const selectCls = `${inputCls} cursor-pointer`;

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.docx,.xlsx';
const MAX_SIZE_BYTES = 20 * 1024 * 1024;

interface Props {
  onClose: () => void;
  onSaved: (doc: DocumentoImpianto) => void;
}

export default function UploadDocumentoImpiantoModal({ onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [nome, setNome] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [tipo, setTipo] = useState<DocumentoImpiantoTipo>('normativa');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = !!file && nome.trim().length > 0 && !submitting;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return;
    setError(null);

    if (file.size > MAX_SIZE_BYTES) {
      setError(t('documenti.upload_modal.err_grande'));
      return;
    }

    setSubmitting(true);
    try {
      const saved = await uploadDocumentoImpianto(
        file,
        nome.trim(),
        descrizione.trim() || null,
        tipo,
      );
      onSaved(saved);
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: { detail?: string } } })?.response;
      if (resp?.status === 413) {
        setError(t('documenti.upload_modal.err_413'));
      } else if (resp?.status === 400) {
        setError(resp.data?.detail ?? t('documenti.upload_modal.err_tipo'));
      } else {
        setError(resp?.data?.detail ?? t('documenti.upload_modal.err_generico'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="upload-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid">
          <h2 className="text-base font-semibold text-ink-dark flex items-center gap-2">
            <FileText size={16} className="text-sage" />
            {t('documenti.upload_modal.titolo')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-soft hover:text-ink-dark"
            data-testid="upload-modal-cancel"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('documenti.upload_modal.file')}</label>
              <input
                type="file"
                accept={ACCEPT}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-ink-dark"
                data-testid="upload-modal-file-input"
              />
              <p className="text-[11px] text-ink-faint mt-1">
                {t('documenti.upload_modal.tipi_accettati')}
              </p>
              <p className="text-[11px] text-ink-faint">{t('documenti.upload_modal.dim_max')}</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('documenti.upload_modal.nome')}</label>
              <input
                type="text"
                maxLength={255}
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className={inputCls}
                placeholder={t('documenti.upload_modal.nome_placeholder')}
                data-testid="upload-modal-nome"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('documenti.upload_modal.tipo')}</label>
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value as DocumentoImpiantoTipo)}
                className={selectCls}
                data-testid="upload-modal-tipo"
              >
                <option value="normativa">{t('documenti.tipo.normativa')}</option>
                <option value="scheda_tecnica">{t('documenti.tipo.scheda_tecnica')}</option>
                <option value="autorizzazione">{t('documenti.tipo.autorizzazione')}</option>
                <option value="altro">{t('documenti.tipo.altro')}</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('documenti.upload_modal.descrizione')}</label>
              <textarea
                value={descrizione}
                onChange={(e) => setDescrizione(e.target.value)}
                rows={3}
                className={inputCls}
                placeholder={t('documenti.upload_modal.descrizione_placeholder')}
                data-testid="upload-modal-descrizione"
              />
            </div>

            {error && (
              <div
                className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger"
                data-testid="upload-modal-error"
              >
                {error}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-mid bg-stone-bg">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50"
            >
              {t('common.azioni.annulla')}
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60"
              data-testid="upload-modal-submit"
            >
              {submitting ? t('documenti.upload_modal.caricamento') : t('documenti.upload_modal.carica')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
